import { defaultContractRoles, defaultContractRolesWithPartner } from './defaults'
import * as R from 'ramda'
import { ContractRole, Partner, PartnerCustomDetails } from '../../types/types'

const partnerRolesDefault = ['Sold-to Party', 'Bill-to Party', 'Payer']
const partnerRoles = {
  decidedBy: 'decidedBy',
  soldTo: 'soldTo',
  payer: 'payer',
  billedTo: 'billedTo'
}

export function getUniquePartnerRoles(partners: Partner[] | null | undefined): Partner[] {
  if (isPartnersEmpty(partners)) {
    return []
  }
  const partnersList = partners?.filter(partner => partner.accountNumber !== null)
  const filteredPartnerList: Partner[] = partnerRolesDefault
    .map(role => partnersList!.find(partner => partner.role === role) as Partner)
    .filter(partner => partner !== undefined)
  return R.uniqBy(partner => (partner.accountNumber !== null ? partner.accountNumber : ''), filteredPartnerList)
}

export const isPartnerMapped = (contractRole: ContractRole[]) =>
  contractRole?.some(role => role.accountNumber !== '' || role.accountNumber !== undefined)

export function getContractPartnersRole(partners: Partner[] | null | undefined) {
  if (isPartnersEmpty(partners)) {
    return defaultContractRoles
  }
  const partnersList: Partner[] = filterPartnersByRolesFromCRMList(partners) as Partner[]
  const firstPartner = R.head(partnersList)
  const getAllRoleOtherThanFirstPartner = getFlattenedRolesForThePartners(R.remove(0, 1, partnersList))
  // Adding decidedBy as it should not be set on Partner
  getAllRoleOtherThanFirstPartner.push(partnerRoles.decidedBy)
  const contractRoles: ContractRole[] = defaultContractRolesWithPartner.map(contractRole => {
    const partnerData = partnersList!.find(partner => getRoleNames(partner.role) === contractRole.role)
    const isSingleOrFirstPartner =
      !getAllRoleOtherThanFirstPartner.includes(contractRole.role) || partnersList.length === 1
    return {
      ...contractRole,
      accountNumber: isSingleOrFirstPartner ? firstPartner?.accountNumber : partnerData?.accountNumber
    } as ContractRole
  }) as ContractRole[]
  return contractRoles
}

function filterPartnersByRolesFromCRMList(partners: Partner[] | null | undefined) {
  if (isPartnersEmpty(partners)) {
    return []
  }
  const partnersList = partners!.filter(
    partner => partner.accountNumber !== undefined && partner.accountNumber !== null
  )
  return partnerRolesDefault
    .map(role => partnersList.find(partner => partner.role === role) as Partner)
    .filter(partner => partner !== undefined)
}

function getFlattenedRolesForThePartners(partners: Partner[]) {
  const partnerDetails = mergePartnerWithDifferntRole(partners)
  return R.flatten(
    partnerDetails.map(partnersData => partnersData.map(partner => getRoleNames(partner.role)))
  ) as string[]
}

function mergePartnerWithDifferntRole(partners: Partner[]) {
  const filteredPartnerList: Partner[] = filterPartnersByRolesFromCRMList(partners)
  const groupPartnerWithRole: Partner[][] = []
  R.uniq(R.pluck('accountNumber', filteredPartnerList)).forEach(partner => {
    groupPartnerWithRole.push(filteredPartnerList.filter(partnerData => partnerData.accountNumber === partner))
  }, filteredPartnerList)
  return groupPartnerWithRole
}

function isPartnersEmpty(partners: Partner[] | null | undefined) {
  return R.isNil(partners) || partners!.filter(partner => !R.isNil(partner)).length === 0
}

function getRoleNames(roleMap: string | null) {
  switch (roleMap) {
    case 'Sold-to Party':
      return partnerRoles.soldTo
    case 'Bill-to Party':
      return partnerRoles.billedTo
    case 'Payer':
      return partnerRoles.payer
    default:
      return ''
  }
}

export function getPartnerFreeText(partnerFreeTextList: PartnerCustomDetails[] | null, accountNumber: string | null) {
  return (
    partnerFreeTextList &&
    partnerFreeTextList.find((freeText: PartnerCustomDetails) => freeText.accountNumber === accountNumber)
  )
}

export function getPartnerByAccountNumber(partners: Partner[], accountNumber: string | null) {
  return accountNumber && partners && partners.find(partner => partner.accountNumber === accountNumber)
}
